// Base API Client with Security Features

import { API_CONFIG, SECURITY_HEADERS, ERROR_CODES } from "./config";
import { tokenManager } from "@/utils/token-manager";
import type { ApiError } from "@/types/api";
import { ROOT } from "@/routes";
import * as Sentry from "@sentry/react";
import { getClientPlatform } from "@/platform";
import { summarizeEndpointCounts, type RateLimitRequest } from "./rateLimitDiagnostics";

const CLIENT_PLATFORM = getClientPlatform();

function createRequestId(): string {
  const configured = SECURITY_HEADERS["x-request-id"];
  if (typeof configured === "function") {
    return configured();
  }
  return crypto.randomUUID();
}

// Custom error class for API errors
export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public status?: number,
    public details?: any,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

// Token management - using centralized token manager

interface RateLimitWindowSnapshot {
  minuteCount: number;
  hourCount: number;
  minuteLimit: number;
  hourLimit: number;
  minuteEndpointCounts: Record<string, number>;
  topMinuteEndpoints: Array<{ endpoint: string; count: number }>;
}

// Rate limiting implementation
class RateLimiter {
  private requests: RateLimitRequest[] = [];

  private prune(now: number) {
    const oneHourAgo = now - 3600000;
    this.requests = this.requests.filter(req => req.time > oneHourAgo);
  }

  getWindowSnapshot(now = Date.now()): RateLimitWindowSnapshot {
    const oneMinuteAgo = now - 60000;
    this.prune(now);

    const requestsLastMinute = this.requests.filter(req => req.time > oneMinuteAgo);
    const { counts, topEndpoints } = summarizeEndpointCounts(requestsLastMinute);

    return {
      minuteCount: requestsLastMinute.length,
      hourCount: this.requests.length,
      minuteLimit: API_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_MINUTE,
      hourLimit: API_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_HOUR,
      minuteEndpointCounts: counts,
      topMinuteEndpoints: topEndpoints,
    };
  }

  canMakeRequest(): boolean {
    const snapshot = this.getWindowSnapshot();
    const countLastMinute = snapshot.minuteCount;
    const countLastHour = snapshot.hourCount;

    // Debug: log when approaching limit (80%)
    if (countLastMinute >= API_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_MINUTE * 0.8) {
      console.warn(`[RateLimiter] ${countLastMinute}/${API_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_MINUTE} requests in last minute. Breakdown:`, snapshot.minuteEndpointCounts);
    }

    if (countLastMinute >= API_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_MINUTE) {
      console.error(`[RateLimiter] RATE LIMIT HIT! ${countLastMinute} requests in last minute. Breakdown:`, snapshot.minuteEndpointCounts);
      return false;
    }

    if (countLastHour >= API_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_HOUR) {
      return false;
    }

    return true;
  }

  recordRequest(endpoint?: string) {
    this.requests.push({ time: Date.now(), endpoint });
  }
}

// Main API Client
export class ApiClient {
  private static instance: ApiClient;
  private rateLimiter: RateLimiter;

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  constructor() {
    this.rateLimiter = new RateLimiter();
  }

  // Main request method with security features
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipAuth = false,
    isRetryAfterRefresh = false,
    requestGuard?: () => boolean,
  ): Promise<T> {
    if (requestGuard && !requestGuard()) throw new DOMException("Request no longer current", "AbortError");
    // Check rate limiting
    if (!this.rateLimiter.canMakeRequest()) {
      const snapshot = this.rateLimiter.getWindowSnapshot();
      const limitType = snapshot.minuteCount >= snapshot.minuteLimit ? "minute" : "hour";
      Sentry.addBreadcrumb({
        category: "api.rate_limit",
        message: "Client request budget exhausted before request",
        level: "warning",
        data: {
          endpoint,
          limitType,
          minuteCount: snapshot.minuteCount,
          minuteLimit: snapshot.minuteLimit,
          hourCount: snapshot.hourCount,
          hourLimit: snapshot.hourLimit,
          topMinuteEndpoints: snapshot.topMinuteEndpoints,
        },
      });
      throw new ApiClientError("Rate limit exceeded. Please try again later.", ERROR_CODES.RATE_LIMITED, 429);
    }

    // Build headers - use lowercase for API Gateway v2 compatibility
    const requestId = createRequestId();
    const headers: Record<string, string> = {
      "content-type": "application/json",
      "x-request-id": requestId,
      ...(options.headers as Record<string, string> | undefined),
    };
    const apiKey = SECURITY_HEADERS["x-api-key"];
    if (apiKey) {
      headers["x-api-key"] = apiKey;
    }

    // Platform header — read server-side by detect_source_platform() so
    // activity rows record whether a write came from web/ios/android.
    headers["x-stasht-client-platform"] = CLIENT_PLATFORM;

    // Add auth token if needed
    if (!skipAuth) {
      const accessToken = await tokenManager.ensureFreshAccessToken();
      if (!accessToken) {
        throw new ApiClientError("No authentication token", ERROR_CODES.UNAUTHORIZED, 401);
      }
      headers["authorization"] = `Bearer ${accessToken}`;
    }
    if (requestGuard && !requestGuard()) throw new DOMException("Request no longer current", "AbortError");

    // Prepare request
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const method = options.method || "GET";

    console.log("🌐 ApiClient request:", {
      baseUrl: API_CONFIG.BASE_URL,
      endpoint: endpoint,
      fullUrl: url,
      method,
      requestId,
    });

    Sentry.addBreadcrumb({
      category: "api",
      message: "API request started",
      level: "info",
      data: {
        endpoint,
        method,
        requestId,
      },
    });

    try {
      // Record request for rate limiting
      this.rateLimiter.recordRequest(endpoint);

      // Make request with retry logic - pass headers without signal
      const response = await this.fetchWithRetry(url, {
        ...options,
        headers,
      }, 1, requestGuard);
      const responseRequestId = response.headers.get("x-stasht-request-id") || response.headers.get("x-request-id") || requestId;

      // Handle response
      if (!response.ok) {
        // Handle 401 Unauthorized - try to refresh token and retry once
        if (response.status === 401 && !skipAuth && !isRetryAfterRefresh) {
          if (requestGuard && !requestGuard()) throw new DOMException("Request no longer current", "AbortError");
          try {
            await tokenManager.refreshAccessToken();
            return this.request<T>(endpoint, options, skipAuth, true, requestGuard);
          } catch (refreshError) {
            if (options.signal?.aborted || (requestGuard && !requestGuard()) ||
                (refreshError instanceof Error && refreshError.name === "AbortError")) throw refreshError;
            console.error("Token refresh failed on 401:", refreshError);
            if (refreshError instanceof Error) {
              const status = (refreshError as { status?: number }).status;
              // Only clear tokens if refresh endpoint explicitly rejected the refresh token (401/403)
              // Preserve tokens on transient failures (network errors, timeouts, 5xx)
              // Guard: never nuke tokens while initial hydration is in progress — the 401
              // is likely because tokens haven't been loaded from Keychain yet, not because
              // the session is invalid.
              if (!tokenManager.isHydrated()) {
                Sentry.addBreadcrumb({
                  category: "auth",
                  message: "Skipping token clear - hydration incomplete",
                  level: "warning",
                  data: { status, endpoint },
                });
              } else if (status === 401 || status === 403) {
                Sentry.addBreadcrumb({
                  category: "auth",
                  message: "Clearing tokens - refresh token rejected",
                  level: "warning",
                  data: { status, endpoint },
                });
                tokenManager.clearAllTokens();
                window.location.href = ROOT();
              } else {
                // Transient failure - preserve refresh token
                Sentry.addBreadcrumb({
                  category: "auth",
                  message: "Token refresh transient failure - preserving refresh token",
                  level: "info",
                  data: {
                    status,
                    errorName: refreshError.name,
                    endpoint,
                  },
                });
              }
            }
            // Still throw the error
          }
        }

        // 403 means "you don't have permission to this resource" but doesn't invalidate the session.
        // Only 401 with failed refresh should trigger logout.

        const errorData = (await response.json().catch(() => ({}))) as ApiError;
        const enrichedErrorData = { ...errorData, requestId: responseRequestId };
        Sentry.addBreadcrumb({
          category: "api",
          message: "API request failed",
          level: "error",
          data: {
            endpoint,
            method,
            status: response.status,
            requestId: responseRequestId,
            code: errorData.code,
          },
        });
        throw new ApiClientError(
          errorData.error || `HTTP ${response.status}`,
          errorData.code || ERROR_CODES.SERVER_ERROR,
          response.status,
          enrichedErrorData,
        );
      }

      return response.json();
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error;
      }

      if (error instanceof Error) {
        Sentry.addBreadcrumb({
          category: "api",
          message: "API request threw",
          level: "error",
          data: {
            endpoint,
            method,
            requestId,
            errorName: error.name,
          },
        });
        if (error.name === "AbortError") {
          if (options.signal?.aborted || (requestGuard && !requestGuard())) throw error;
          throw new ApiClientError("Request timeout", ERROR_CODES.NETWORK_ERROR);
        }
        throw new ApiClientError(error.message, ERROR_CODES.NETWORK_ERROR);
      }

      throw new ApiClientError("Unknown error", ERROR_CODES.SERVER_ERROR);
    }
  }

  // Fetch with exponential backoff retry
  private async fetchWithRetry(url: string, options: RequestInit, attempt = 1, requestGuard?: () => boolean): Promise<Response> {
    if (options.signal?.aborted || (requestGuard && !requestGuard())) {
      throw new DOMException("Request no longer current", "AbortError");
    }
    // Create a new AbortController for this specific attempt
    const attemptController = new AbortController();
    const abortFromCaller = () => attemptController.abort();
    options.signal?.addEventListener("abort", abortFromCaller, { once: true });

    // Set timeout for this specific attempt
    const timeoutId = setTimeout(() => {
      attemptController.abort();
    }, API_CONFIG.TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        signal: attemptController.signal,
      });
      clearTimeout(timeoutId);
      options.signal?.removeEventListener("abort", abortFromCaller);

      // Don't retry client errors (4xx) — except 429 (rate limited), which is retryable
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        return response;
      }

      // Retry server errors (5xx) and 429 (rate limited)
      if ((response.status >= 500 || response.status === 429) && attempt < API_CONFIG.RETRY.MAX_ATTEMPTS) {
        const delay = Math.min(
          API_CONFIG.RETRY.INITIAL_DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_FACTOR, attempt - 1),
          API_CONFIG.RETRY.MAX_DELAY,
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, attempt + 1, requestGuard);
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      options.signal?.removeEventListener("abort", abortFromCaller);
      if (options.signal?.aborted || (requestGuard && !requestGuard())) throw error;

      // Check if it was a timeout
      if (error instanceof Error && error.name === "AbortError") {
        // Retry on timeout if we haven't exceeded max attempts
        if (attempt < API_CONFIG.RETRY.MAX_ATTEMPTS) {
          const delay = Math.min(
            API_CONFIG.RETRY.INITIAL_DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_FACTOR, attempt - 1),
            API_CONFIG.RETRY.MAX_DELAY,
          );
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchWithRetry(url, options, attempt + 1, requestGuard);
        }

        // If we've exhausted retries, throw timeout error
        throw error;
      }

      // Retry other network errors
      if (attempt < API_CONFIG.RETRY.MAX_ATTEMPTS) {
        const delay = Math.min(
          API_CONFIG.RETRY.INITIAL_DELAY * Math.pow(API_CONFIG.RETRY.BACKOFF_FACTOR, attempt - 1),
          API_CONFIG.RETRY.MAX_DELAY,
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithRetry(url, options, attempt + 1, requestGuard);
      }

      throw error;
    }
  }

  // Convenience methods
  async get<T>(endpoint: string, skipAuth = false): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" }, skipAuth);
  }

  async post<T>(endpoint: string, data?: any, skipAuth = false): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      skipAuth,
    );
  }

  async put<T>(endpoint: string, data?: any, skipAuth = false): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      },
      skipAuth,
    );
  }

  async patch<T>(endpoint: string, data?: any, skipAuth = false): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "PATCH",
        body: data ? JSON.stringify(data) : undefined,
      },
      skipAuth,
    );
  }

  async delete<T>(endpoint: string, skipAuth = false): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" }, skipAuth);
  }

  // Token management
  setTokens(accessToken: string, refreshToken?: string) {
    tokenManager.storeTokens(accessToken, refreshToken);
  }

  clearTokens() {
    tokenManager.clearAllTokens();
  }

  getAccessToken(): string | null {
    const tokens = tokenManager.getTokens();
    return tokens ? tokens.accessToken : null;
  }

  async getAuthHeaders(options: { includeApiKey?: boolean } = {}): Promise<HeadersInit> {
    const token = await tokenManager.ensureFreshAccessToken();
    if (!token) {
      throw new ApiClientError("No authentication token", ERROR_CODES.UNAUTHORIZED, 401);
    }

    const headers: HeadersInit = {
      authorization: `Bearer ${token}`,
    };

    if (options.includeApiKey) {
      const apiKey = SECURITY_HEADERS["x-api-key"];
      if (apiKey) {
        headers["x-api-key"] = apiKey;
      }
    }

    return headers;
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();
